import { useCallback, useMemo } from 'react';
import { saveService } from '../services/gameSaveService';
import type { CaseInfo } from '../types';
import type {
  EpisodeUnlockInfo,
  EpisodeUnlockStatus,
  SeasonUnlockInfo,
} from '../types/episode';
import type { SeasonInfo } from '../types/season';
import { getTimeUntilRelease, isReleased } from '../utils/countdownFormat';
import { resolveRequiredEpisode } from '../utils/episodeUnlock';

/**
 * Hook to determine season and episode unlock status
 */
export const useSeasonUnlock = (cases: CaseInfo[], seasons: SeasonInfo[]) => {
  // Get completed level IDs from save service
  const completedLevelIds = useMemo(
    () => saveService.getCompletedLevelIds(),
    [],
  );

  /**
   * Check if a specific level/episode has been completed
   */
  const isLevelCompleted = useCallback(
    (caseId: string): boolean => {
      return completedLevelIds.includes(caseId);
    },
    [completedLevelIds],
  );

  /**
   * Check if all episodes in a season are completed
   */
  const isSeasonCompleted = useCallback(
    (seasonId: string): boolean => {
      const seasonEpisodes = cases.filter((c) => c.seasonId === seasonId);
      if (seasonEpisodes.length === 0) return false;
      return seasonEpisodes.every((e) => isLevelCompleted(e.caseId));
    },
    [cases, isLevelCompleted],
  );

  /**
   * Check if a season is unlocked
   */
  const checkSeasonUnlocked = useCallback(
    (season: SeasonInfo): boolean => {
      // Check release date
      if (!isReleased(season.releaseDate)) {
        return false;
      }

      // Check required season completion
      if (season.requiredSeasonId) {
        if (!isSeasonCompleted(season.requiredSeasonId)) {
          return false;
        }
      }

      return true;
    },
    [isSeasonCompleted],
  );

  /**
   * Get unlock status for a specific episode
   */
  const getEpisodeUnlockStatus = useCallback(
    (caseInfo: CaseInfo, seasonUnlocked: boolean): EpisodeUnlockInfo => {
      const { releaseDate } = caseInfo;

      // Resolve prerequisite: explicit requiredEpisodeId OR default sequential (episode N-1)
      const requiredEpisode = resolveRequiredEpisode(caseInfo, cases);
      const requiredEpisodeId = requiredEpisode?.caseId;

      // If season is not unlocked, episode is locked by season
      if (!seasonUnlocked) {
        return {
          status: 'locked_season',
          caseInfo,
          isPlayable: false,
          resolvedRequiredEpisodeId: requiredEpisodeId,
          releaseDate: releaseDate ? new Date(releaseDate) : null,
          timeUntilRelease: getTimeUntilRelease(releaseDate),
        };
      }

      // Check completion requirement
      const requiredEpisodeCompleted =
        !requiredEpisodeId || isLevelCompleted(requiredEpisodeId);

      // Check release date requirement
      const released = isReleased(releaseDate);

      // Determine status
      let status: EpisodeUnlockStatus;
      if (requiredEpisodeCompleted && released) {
        status = 'unlocked';
      } else if (!requiredEpisodeCompleted && !released) {
        status = 'locked_both';
      } else if (!requiredEpisodeCompleted) {
        status = 'locked_completion';
      } else {
        status = 'locked_release';
      }

      // Derive episode number: use required episode's number, or parse from title, or infer from current episode
      const rawNumber = requiredEpisode?.episodeNumber;
      const parsedFromName = requiredEpisode?.title?.match(/\d+/);
      const inferredFromCurrent =
        caseInfo.episodeNumber != null ? caseInfo.episodeNumber - 1 : undefined;
      const requiredEpisodeNumber =
        rawNumber ??
        (parsedFromName ? parseInt(parsedFromName[0], 10) : undefined) ??
        inferredFromCurrent;

      return {
        status,
        caseInfo,
        isPlayable: status === 'unlocked',
        requiredEpisodeName: requiredEpisode?.title,
        requiredEpisodeNumber,
        resolvedRequiredEpisodeId: requiredEpisodeId,
        releaseDate: releaseDate ? new Date(releaseDate) : null,
        timeUntilRelease: getTimeUntilRelease(releaseDate),
      };
    },
    [cases, isLevelCompleted],
  );

  /**
   * Get unlock info for all seasons with their episodes
   */
  const seasonUnlockInfos = useMemo((): SeasonUnlockInfo[] => {
    return seasons
      .sort((a, b) => a.seasonNumber - b.seasonNumber)
      .map((season) => {
        const seasonUnlocked = checkSeasonUnlocked(season);
        const episodes = cases
          .filter((c) => c.seasonId === season.seasonId)
          .sort((a, b) => (a.episodeNumber || 0) - (b.episodeNumber || 0))
          .map((caseInfo) => getEpisodeUnlockStatus(caseInfo, seasonUnlocked));

        const completedEpisodeCount = episodes.filter((e) =>
          isLevelCompleted(e.caseInfo.caseId),
        ).length;

        // Find required season name
        const requiredSeason = season.requiredSeasonId
          ? seasons.find((s) => s.seasonId === season.requiredSeasonId)
          : undefined;

        return {
          season,
          isUnlocked: seasonUnlocked,
          requiredSeasonName: requiredSeason?.title,
          releaseDate: season.releaseDate ? new Date(season.releaseDate) : null,
          timeUntilRelease: getTimeUntilRelease(season.releaseDate),
          episodes,
          completedEpisodeCount,
          totalEpisodeCount: episodes.length,
        };
      });
  }, [
    seasons,
    cases,
    checkSeasonUnlocked,
    getEpisodeUnlockStatus,
    isLevelCompleted,
  ]);

  /**
   * Check if a specific case is playable
   */
  const isCasePlayable = useCallback(
    (caseId: string): boolean => {
      for (const seasonInfo of seasonUnlockInfos) {
        const episode = seasonInfo.episodes.find(
          (e) => e.caseInfo.caseId === caseId,
        );
        if (episode) {
          return episode.isPlayable;
        }
      }
      return false;
    },
    [seasonUnlockInfos],
  );

  /**
   * Get the season containing a specific case
   */
  const getSeasonForCase = useCallback(
    (caseId: string): SeasonUnlockInfo | undefined => {
      return seasonUnlockInfos.find((s) =>
        s.episodes.some((e) => e.caseInfo.caseId === caseId),
      );
    },
    [seasonUnlockInfos],
  );

  /**
   * Get episode unlock info for a specific case
   */
  const getEpisodeUnlockInfo = useCallback(
    (caseId: string): EpisodeUnlockInfo | undefined => {
      for (const seasonInfo of seasonUnlockInfos) {
        const episode = seasonInfo.episodes.find(
          (e) => e.caseInfo.caseId === caseId,
        );
        if (episode) {
          return episode;
        }
      }
      return undefined;
    },
    [seasonUnlockInfos],
  );

  return {
    seasonUnlockInfos,
    isCasePlayable,
    getSeasonForCase,
    getEpisodeUnlockInfo,
    isLevelCompleted,
    isSeasonCompleted,
  };
};
